(() => {
  const SELECTOR = '[data-scarfhold-looks-board]';
  const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const storageGet = (key) => {
    try { return window.localStorage.getItem(key); } catch (error) { return null; }
  };

  const storageSet = (key, value) => {
    try { window.localStorage.setItem(key, value); } catch (error) { /* Optional browser-session continuity only. */ }
  };

  const makeId = () => {
    if (window.crypto && typeof window.crypto.randomUUID === 'function') return window.crypto.randomUUID();
    return `scarfhold-${Date.now()}-${Math.random().toString(16).slice(2)}`;
  };

  const getSessionId = () => {
    if (window.ScarfholdMiniCapsuleVote?.getSessionId) return window.ScarfholdMiniCapsuleVote.getSessionId();
    const key = 'scarfhold_session_id';
    let id = storageGet(key);
    if (!id) {
      id = makeId();
      storageSet(key, id);
    }
    return id;
  };


  class ScarfholdLooksBoard {
    constructor(root) {
      if (root.dataset.looksBoardReady === 'true') return;
      root.dataset.looksBoardReady = 'true';
      this.root = root;
      this.grid = root.querySelector('[data-looks-grid]');
      this.telemetryEndpoint = root.dataset.pinTelemetryEndpoint || '';
      this.cards = () => Array.from(this.grid?.querySelectorAll('[data-look-card]') || []);
      this.resizeFrame = 0;
      this.onClick = this.onClick.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.scheduleLayout = this.scheduleLayout.bind(this);
      root.addEventListener('click', this.onClick);
      root.addEventListener('keydown', this.onKeydown);
      this.imagesReady();
      this.resizeObserver = new ResizeObserver(this.scheduleLayout);
      this.resizeObserver.observe(this.grid);
      this.cards().forEach((card) => this.resizeObserver.observe(card.querySelector('[data-look-surface]')));
      this.scheduleLayout();
      this.lightbox = null;
    }

    imagesReady() {
      this.root.querySelectorAll('img').forEach((image) => {
        if (!image.complete) image.addEventListener('load', this.scheduleLayout, { once: true });
      });
    }

    scheduleLayout() {
      cancelAnimationFrame(this.resizeFrame);
      this.resizeFrame = requestAnimationFrame(() => this.layout());
    }

    layout() {
      if (!this.grid) return;
      const styles = getComputedStyle(this.grid);
      const row = parseFloat(styles.gridAutoRows) || 8;
      const gap = parseFloat(styles.rowGap) || 0;
      const cards = this.cards();
      const anchoredCard = this.root.querySelector('[data-look-card].is-expanded[data-anchor-row]');

      if (anchoredCard) {
        anchoredCard.style.gridColumn = `${anchoredCard.dataset.anchorColumn} / span ${anchoredCard.dataset.anchorSpan}`;
        anchoredCard.style.gridRowStart = anchoredCard.dataset.anchorRow;
      }

      const rowSpans = new Map(cards.map((card) => {
        const surface = card.querySelector('[data-look-surface]');
        const height = surface?.getBoundingClientRect().height || 0;
        return [card, Math.max(1, Math.ceil((height + gap) / (row + gap)))];
      }));

      cards.forEach((card) => {
        card.style.gridRowEnd = `span ${rowSpans.get(card)}`;
      });

      if (anchoredCard) {
        anchoredCard.style.gridColumn = `${anchoredCard.dataset.anchorColumn} / span ${anchoredCard.dataset.anchorSpan}`;
        anchoredCard.style.gridRowStart = anchoredCard.dataset.anchorRow;
      }
    }

    positions() {
      return new Map(this.cards().map((card) => [card.id, card.getBoundingClientRect()]));
    }

    animateFrom(first) {
      if (reducedMotion()) return;
      this.cards().forEach((card) => {
        const start = first.get(card.id);
        if (!start) return;
        const end = card.getBoundingClientRect();
        const x = start.left - end.left;
        const y = start.top - end.top;
        if (Math.abs(x) < 1 && Math.abs(y) < 1) return;
        card.animate([{ transform: `translate(${x}px, ${y}px)` }, { transform: 'none' }], {
          duration: 360,
          easing: 'cubic-bezier(.2,.7,.2,1)'
        });
      });
    }

    anchorFor(card) {
      const gridStyles = getComputedStyle(this.grid);
      const gridRect = this.grid.getBoundingClientRect();
      const cardRect = card.getBoundingClientRect();
      const columns = gridStyles.gridTemplateColumns.split(' ').filter(Boolean).length || 1;
      const columnGap = parseFloat(gridStyles.columnGap) || 0;
      const rowHeight = parseFloat(gridStyles.gridAutoRows) || 8;
      const rowGap = parseFloat(gridStyles.rowGap) || 0;
      const columnWidth = (gridRect.width - columnGap * (columns - 1)) / columns;
      const originalColumn = Math.round((cardRect.left - gridRect.left) / (columnWidth + columnGap)) + 1;
      const span = window.matchMedia('(min-width: 750px)').matches ? Math.min(2, columns) : columns;

      return {
        columnStart: Math.max(1, Math.min(originalColumn, columns - span + 1)),
        rowStart: Math.max(1, Math.round((cardRect.top - gridRect.top) / (rowHeight + rowGap)) + 1),
        span,
        direction: originalColumn > Math.max(1, Math.min(originalColumn, columns - span + 1)) ? 'left' : 'right'
      };
    }

    setExpanded(card, expanded, anchor = null) {
      if (expanded && anchor) {
        card.style.gridColumn = `${anchor.columnStart} / span ${anchor.span}`;
        card.style.gridRowStart = String(anchor.rowStart);
        card.dataset.anchorColumn = String(anchor.columnStart);
        card.dataset.anchorRow = String(anchor.rowStart);
        card.dataset.anchorSpan = String(anchor.span);
        card.classList.toggle('expands-left', anchor.direction === 'left');
        card.classList.toggle('expands-right', anchor.direction === 'right');
      } else if (!expanded) {
        card.style.gridColumn = '';
        card.style.gridRowStart = '';
        delete card.dataset.anchorColumn;
        delete card.dataset.anchorRow;
        delete card.dataset.anchorSpan;
        card.classList.remove('expands-left', 'expands-right');
      }
      card.classList.toggle('is-expanded', expanded);
      card.querySelector('[data-look-open]')?.setAttribute('aria-expanded', String(expanded));
      card.querySelector('[data-look-details]')?.setAttribute('aria-hidden', String(!expanded));
    }

    activeFilters() {
      const filters = {};
      const params = new URLSearchParams(window.location.search);
      params.forEach((value, key) => {
        if (key.startsWith('filter.') || key.startsWith('pinwall_')) {
          if (!filters[key]) filters[key] = [];
          filters[key].push(value);
        }
      });
      this.root.querySelectorAll('[data-pinwall-filter][aria-pressed="true"], [data-pinwall-filter]:checked').forEach((control) => {
        const key = control.name || control.dataset.pinwallFilter || 'filter';
        const value = control.value || control.dataset.value || control.textContent.trim();
        if (!filters[key]) filters[key] = [];
        if (value && !filters[key].includes(value)) filters[key].push(value);
      });
      return JSON.stringify(filters);
    }

    recordOpen(card) {
      if (!this.telemetryEndpoint || !card) return;
      const payload = new URLSearchParams({
        event_type: 'pin_open',
        event_version: '1',
        session_id: getSessionId(),
        look_id: card.dataset.lookId || '',
        look_handle: card.dataset.lookHandle || '',
        look_name: card.dataset.lookName || '',
        pattern_edition_id: card.dataset.patternEditionId || '',
        pattern_edition_name: card.dataset.patternEditionName || '',
        active_filters: this.activeFilters(),
        page_path: window.location.pathname,
        document_referrer: document.referrer || ''
      });

      try {
        if (navigator.sendBeacon && navigator.sendBeacon(this.telemetryEndpoint, payload)) return;
      } catch (error) { /* Fall through to the non-blocking fetch transport. */ }

      fetch(this.telemetryEndpoint, {
        method: 'POST',
        mode: 'no-cors',
        credentials: 'omit',
        keepalive: true,
        body: payload
      }).catch(() => {});
    }

    change(openCard, returnFocus = false) {
      const first = this.positions();
      const current = this.root.querySelector('[data-look-card].is-expanded');
      const opening = Boolean(openCard && !openCard.classList.contains('is-expanded'));
      const anchor = opening ? this.anchorFor(openCard) : null;
      if (current && current !== openCard) this.setExpanded(current, false);
      if (openCard) this.setExpanded(openCard, opening, anchor);
      if (opening) this.recordOpen(openCard);
      this.layout();
      requestAnimationFrame(() => {
        this.layout();
        this.animateFrom(first);
        if (returnFocus && current) current.querySelector('[data-look-open]')?.focus();
      });
    }

    onClick(event) {
      const lightboxImage = event.target.closest('[data-look-lightbox-image] img');
      if (lightboxImage && lightboxImage.closest('[data-look-card].is-expanded')) {
        event.preventDefault();
        event.stopPropagation();
        if (window.matchMedia('(min-width: 750px)').matches) this.openLightbox(lightboxImage);
        return;
      }
      const open = event.target.closest('[data-look-open]');
      const close = event.target.closest('[data-look-close]');
      if (open && this.root.contains(open)) this.change(open.closest('[data-look-card]'));
      if (close && this.root.contains(close)) {
        const card = close.closest('[data-look-card]');
        this.change(card);
        requestAnimationFrame(() => card.querySelector('[data-look-open]')?.focus());
      }
    }

    onKeydown(event) {
      if (event.key !== 'Escape') return;
      if (this.lightbox) {
        this.closeLightbox();
        return;
      }
      const current = this.root.querySelector('[data-look-card].is-expanded');
      if (current) {
        this.change(current);
        requestAnimationFrame(() => current.querySelector('[data-look-open]')?.focus());
      }
    }

    openLightbox(image) {
      const overlay = document.createElement('div');
      overlay.className = 'scarfhold-look-lightbox';
      overlay.setAttribute('role', 'dialog');
      overlay.setAttribute('aria-modal', 'true');
      overlay.setAttribute('aria-label', image.alt || 'Look image');
      overlay.innerHTML = `<button type="button" class="scarfhold-look-lightbox__close" aria-label="Close image">&times;</button><img class="scarfhold-look-lightbox__image" alt="">`;
      const enlarged = overlay.querySelector('img');
      enlarged.src = image.currentSrc || image.src;
      enlarged.alt = image.alt || '';
      overlay.addEventListener('click', (event) => { if (event.target === overlay || event.target.closest('.scarfhold-look-lightbox__close')) this.closeLightbox(); });
      document.body.appendChild(overlay);
      document.body.classList.add('overflow-hidden');
      this.lightbox = overlay;
      overlay.querySelector('button').focus();
    }

    closeLightbox() {
      if (!this.lightbox) return;
      this.lightbox.remove();
      this.lightbox = null;
      document.body.classList.remove('overflow-hidden');
    }
  }

  const init = (scope = document) => scope.querySelectorAll(SELECTOR).forEach((root) => new ScarfholdLooksBoard(root));
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
